import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { compact } from 'lodash-es';
import { catchError, exhaustMap, map, mergeMap, of, switchMap, tap } from 'rxjs';

import { CounterpartyService, CustomerService, FinancialAccountService } from '@shared/services';

import { FinancialAccountDetailsActions } from './financial-account.actions';
import { selectRouteData, selectRouteParams, selectUrl } from '../core';
import { MessagesActions } from '../core/messages';

export const loadFinancialAccountDetails$ = createEffect(
  (
    actions$ = inject(Actions),
    financialAccountService = inject(FinancialAccountService),
    counterpartyService = inject(CounterpartyService),
    customerService = inject(CustomerService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(FinancialAccountDetailsActions.loadFinancialAccountDetails),
      concatLatestFrom(() => [store.select(selectRouteParams), store.select(selectRouteData)]),
      mergeMap(([, routeParams, routeData]) => {
        if (routeData.isEscrowAccount) {
          return financialAccountService.getEscrowFinancialAccountById(routeParams.id);
        } else {
          return financialAccountService.getFinancialAccountDetailsWithBalances(routeParams.id);
        }
      }),
      exhaustMap((financialAccount) => {
        if (financialAccount.accountHolderId && financialAccount.accountHolderType === 'COUNTERPARTY') {
          return counterpartyService.getCounterpartyById(financialAccount.accountHolderId).pipe(
            map((recipient) => {
              financialAccount.displayName = compact([recipient.firstName, recipient.lastName]).join(' ');
              return financialAccount;
            }),
            catchError(() => of(financialAccount)),
          );
        } else if (financialAccount.accountHolderId && financialAccount.accountHolderType === 'CUSTOMER') {
          return customerService.getCustomerById(financialAccount.accountHolderId).pipe(
            map((customer) => {
              financialAccount.displayName = compact([customer.firstName, customer.lastName]).join(' ');
              return financialAccount;
            }),
            catchError(() => of(financialAccount)),
          );
        } else {
          return of(financialAccount);
        }
      }),
      map((financialAccountDetails) => {
        return FinancialAccountDetailsActions.loadFinancialAccountDetailsSuccess({ financialAccountDetails });
      }),
      catchError((error) => {
        return of(FinancialAccountDetailsActions.loadFinancialAccountDetailsFailure({ error }));
      }),
    );
  },
  { functional: true },
);

export const loadFinancialAccountDetailsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(FinancialAccountDetailsActions.loadFinancialAccountDetailsFailure),
      switchMap(() => of(MessagesActions.displayError({ message: 'Unable to fetch Financial Account Details.' }))),
    );
  },
  { functional: true },
);

export const loadFinancialAccountDetailsFailureRedirect$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), router = inject(Router)) => {
    return actions$.pipe(
      ofType(FinancialAccountDetailsActions.loadFinancialAccountDetailsFailure),
      concatLatestFrom(() => [store.select(selectUrl), store.select(selectRouteParams)]),
      tap({
        next: ([, currentUrl, routeParams]) => {
          const prevUrl = currentUrl
            .split('/')
            .slice(0, routeParams.exceptionId ? -3 : -1)
            .join('/');
          router.navigateByUrl(prevUrl);
        },
      }),
    );
  },
  { functional: true, dispatch: false },
);
